// ENGINE TWIN (#97) — this file exists twice: `src/…` is the source, and
// `functions/engine/…` is a generated copy that the Cloud Functions run.
// Never edit the copy. After changing this file run `yarn sync-engine`, or
// the drift guard in `functions/scripts/engineSync.test.js` fails the suite.

/**
 * #542: a rest day the engine cannot read is a card to fix, not a crash.
 *
 * #505 taught the engine to REFUSE a `restDay` it cannot read rather than
 * default it to Saturday — the week boundary decides which hours are overtime,
 * so guessing for an employee whose card says something else is a wrong wage
 * that looks finished. That decision is right and stays. What reached the
 * employer was not: the refusal arrived at the generic error boundary as
 * "אופס! משהו השתבש — אירעה שגיאה בלתי צפויה", with
 * `restDay "0" אינו יום מנוחה שבועי` printed underneath.
 *
 * Three things were false at once. The failure is entirely EXPECTED — the
 * engine detected a bad value and refused on purpose. `restDay` is a field name
 * in the code, not one the employer has ever seen. And both offers, "נסה שוב"
 * and "חזרה לדף הבית", lead nowhere: the only thing that fixes it is one field
 * on the employee card, which is the one thing the screen did not say.
 *
 * So the refusal travels as its own type, carrying WHO it is about, and the
 * screens render it as an instruction. The technical message stays on
 * `error.message` for the console and the copied error report; the employer
 * reads `invalidRestDayText`, whose wording is Shai's decision on this ticket
 * and not ours.
 *
 * No imports, deliberately: this file is copied into `functions/engine/`
 * (`functions/scripts/engineFiles.mjs`) because the engine throws it.
 */

export const INVALID_REST_DAY = 'InvalidRestDayError'

export class InvalidRestDayError extends Error {
  constructor(message, value) {
    super(message)
    this.name = INVALID_REST_DAY
    this.value = value
  }
}

export const isInvalidRestDay = error => error instanceof InvalidRestDayError ||
  error?.name === INVALID_REST_DAY

/**
 * Attach the employee the refusal is about.
 *
 * The engine knows the VALUE and nothing else — `resolveRestDay` receives one
 * field. Only the caller holds the calculation, and by the time the error has
 * crossed a screen boundary there is nothing left to identify. Anything that is
 * not this refusal passes through untouched.
 */
export function withEmployee(error, calculation) {
  if (!isInvalidRestDay(error) || !calculation) {
    return error
  }
  error.firstName = error.firstName ?? calculation.firstName
  error.lastName = error.lastName ?? calculation.lastName
  return error
}

/** The employee's name as the employer knows it, or '' when we were not told. */
export const employeeNameOf = error => [error?.firstName, error?.lastName]
  .filter(part => typeof part === 'string' && part.trim())
  .join(' ')
  .trim()

/**
 * What the employer reads. Shai's wording (#542), verbatim.
 *
 * The name is dropped rather than replaced by a placeholder when we do not have
 * it: "לא ניתן לחשב לעובד — ..." still says what is wrong and where, and an
 * employer who reads a literal `[שם]` learns only that something is broken.
 */
export const invalidRestDayText = name =>
  `לא ניתן לחשב לעובד${name ? ` ${name}` : ''} — יום המנוחה בכרטיס שלו אינו תקין`
